import { ChangeDetectionStrategy, Component, computed, inject, signal, OnInit } from '@angular/core';
import { JsonPipe } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { switchMap, of } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  AccordionComponent,
  AccordionItemComponent,
  EntityDetailLayoutComponent,
  ErrorStateComponent,
  LoadingStateComponent,
  ProfileDangerAction,
  ProfileQuickAction,
  ProfileStat,
  ProfileSummaryCardComponent,
  ZagButton,
  ZagIcon,
  FormattingService,
} from '@zag/ui';
import { type NotificationResponse, type DeliveryResponse } from '@zag/notifications/common';
import { NotificationsService } from '../../services/notifications.service';

/**
 * Full notification detail page using the standard entity-detail-layout pattern:
 * - Left sidebar: profile summary card with type icon, title, stats (dates, category)
 *   and quick/danger actions (resend, archive)
 * - Right main: accordion with Message, Delivery Status, and Metadata sections
 *
 * Route: /notifications/:id
 */
@Component({
  selector: 'zag-notification-detail',
  imports: [
    TranslatePipe,
    ZagIcon,
    ZagButton,
    RouterModule,
    JsonPipe,
    EntityDetailLayoutComponent,
    ProfileSummaryCardComponent,
    AccordionComponent,
    AccordionItemComponent,
    LoadingStateComponent,
    ErrorStateComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './notification-detail.component.html',
  styleUrls: ['./notification-detail.component.scss'],
})
export class NotificationDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly notificationsService = inject(NotificationsService);
  private readonly formattingService = inject(FormattingService);
  private readonly translate = inject(TranslateService);
  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  readonly notification = signal<NotificationResponse | null>(null);
  readonly deliveries = signal<DeliveryResponse[]>([]);
  readonly isLoading = signal(true);
  readonly error = signal<string | null>(null);
  readonly isResending = signal(false);

  readonly notificationId = toSignal(this.route.paramMap.pipe(switchMap(params => of(params.get('id') ?? ''))));

  readonly typeIcon = computed(() => {
    switch (this.notification()?.type) {
      case 'success':
        return 'check_circle';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  });

  /** Icon background color mapped from notification type */
  readonly typeIconColor = computed(() => {
    switch (this.notification()?.type) {
      case 'success':
        return 'var(--color-success)';
      case 'warning':
        return 'var(--color-warning)';
      case 'error':
        return 'var(--color-error)';
      default:
        return 'var(--color-info)';
    }
  });

  /** Category badge shown on the avatar in the profile card */
  readonly categoryBadge = computed<string | undefined>(() => this.notification()?.category ?? undefined);

  /**
   * Optional interpolation parameters for the title/message i18n keys —
   * dispatched under `metadata.translateParams` (e.g. the reviewer comment of
   * a rejected time-tracking day). Absent for plain static notifications.
   */
  protected translateParams(): Record<string, unknown> | undefined {
    const params = this.notification()?.metadata?.['translateParams'];
    return typeof params === 'object' && params !== null ? (params as Record<string, unknown>) : undefined;
  }

  /** Stats shown in the profile sidebar card */
  readonly profileStats = computed<ProfileStat[]>(() => {
    this.currentLang();
    const n = this.notification();
    if (!n) return [];
    const stats: ProfileStat[] = [
      {
        label: this.translate.instant('notifications.detail.createdAt'),
        value: this.formatDate(n.createdAt),
        icon: 'schedule',
      },
    ];
    if (n.readAt) {
      stats.push({
        label: this.translate.instant('notifications.detail.readAt'),
        value: this.formatDate(n.readAt),
        icon: 'done_all',
      });
    }
    if (n.expiresAt) {
      stats.push({
        label: this.translate.instant('notifications.detail.expiresAt'),
        value: this.formatDate(n.expiresAt),
        icon: 'timer_off',
      });
    }
    return stats;
  });

  /** Quick actions in the profile sidebar */
  readonly quickActions = computed<ProfileQuickAction[]>(() => {
    this.currentLang();
    const n = this.notification();
    if (!n) return [];
    const actions: ProfileQuickAction[] = [
      { id: 'resend', label: this.translate.instant('notifications.resend'), icon: 'send', disabled: this.isResending() },
    ];
    if (!n.read) {
      actions.unshift({ id: 'markAsRead', label: this.translate.instant('notifications.markAsRead'), icon: 'done' });
    }
    const actionUrl = n.metadata?.['actionUrl'];
    if (typeof actionUrl === 'string' && actionUrl) {
      actions.push({ id: 'openContext', label: this.translate.instant('notifications.openContext'), icon: 'open_in_new' });
    }
    return actions;
  });

  /** Danger zone actions in the profile sidebar */
  readonly dangerActions = computed<ProfileDangerAction[]>(() => {
    this.currentLang();
    if (!this.notification()) return [];
    return [{ id: 'archive', label: this.translate.instant('notifications.archive'), icon: 'archive' }];
  });

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.router.navigate(['/notifications']);
      return;
    }
    this.loadNotification(id);
    this.loadDeliveries(id);
  }

  private loadNotification(id: string): void {
    this.isLoading.set(true);
    this.notificationsService.getNotification(id).subscribe({
      next: notification => {
        this.notification.set(notification);
        this.isLoading.set(false);
        if (!notification.read) {
          this.notificationsService.markAsRead(id).subscribe();
        }
      },
      error: () => {
        this.error.set('notifications.errors.notFound');
        this.isLoading.set(false);
      },
    });
  }

  private loadDeliveries(id: string): void {
    this.notificationsService.getDeliveries(id).subscribe({
      next: deliveries => this.deliveries.set(deliveries),
      error: () => {
        /* deliveries are optional — silently ignore */
      },
    });
  }

  onQuickAction(action: ProfileQuickAction): void {
    const id = this.notification()?.id;
    if (!id) return;
    if (action.id === 'resend') {
      this.isResending.set(true);
      this.notificationsService.resend(id).subscribe({
        next: () => this.isResending.set(false),
        error: () => this.isResending.set(false),
      });
    } else if (action.id === 'markAsRead') {
      this.notificationsService.markAsRead(id).subscribe({
        next: updated => this.notification.set(updated),
        error: () => {
          /* silently ignore */
        },
      });
    } else if (action.id === 'openContext') {
      const url = this.notification()?.metadata?.['actionUrl'];
      if (typeof url === 'string' && url) {
        this.router.navigateByUrl(url);
      }
    }
  }

  onDangerAction(action: ProfileDangerAction): void {
    const id = this.notification()?.id;
    if (!id) return;
    if (action.id === 'archive') {
      this.notificationsService.archive(id).subscribe({
        next: () => {
          this.router.navigate(['/notifications']).catch(() => undefined);
        },
        error: () => {
          /* silently ignore */
        },
      });
    }
  }

  onRetryDelivery(deliveryId: string): void {
    this.notificationsService.retryDelivery(deliveryId).subscribe({
      next: updated => {
        this.deliveries.update(prev => prev.map(d => (d.id === updated.id ? updated : d)));
      },
      error: () => {
        /* silently ignore */
      },
    });
  }

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '—';
    return this.formattingService.formatDateTime(dateStr);
  }
}
